import React, { useState, useMemo } from "react";
import { GameState, PlayerState } from "../../../core/protocol/types";
import ResultScreenLayout from "./ResultScreenLayout";
import ResultHeader from "./ResultHeader";
import ResultTabs from "./ResultTabs";
import ResultFooter from "./ResultFooter";
import RankTable from "./tables/RankTable";
import SpeedTable from "./tables/SpeedTable";
import "../ResultScreen.css";

type SortColumn = "frags" | "deaths" | "score" | "lines";
type PageType = "rank" | "speed";

interface TeamStats {
  frags: number;
  deaths: number;
  score: number;
  lines: number;
  players: number;
  roundWins?: number;
}

interface MultiPlayerResultScreenProps {
  gameState: GameState;
  onQuit: () => void;
  onRestart?: () => void;
}

const MultiPlayerResultScreen: React.FC<MultiPlayerResultScreenProps> = ({
  gameState,
  onQuit,
  onRestart,
}) => {
  const [activeSort, setActiveSort] = useState<SortColumn>("frags");
  const [activePage, setActivePage] = useState<PageType>("rank");

  const teamStats = useMemo(() => {
    const stats = new Map<number, TeamStats>();

    for (let i = 0; i < 8; i++) {
      stats.set(i, {
        frags: 0,
        deaths: 0,
        score: 0,
        lines: 0,
        players: 0,
        roundWins: 0,
      });
    }

    gameState.players.forEach((player) => {
      if (player.isDedicatedServer) return;
      const team = player.team ?? 0;
      const teamStat = stats.get(team);
      if (teamStat) {
        teamStat.frags += player.frags || 0;
        teamStat.deaths += player.deaths || 0;
        teamStat.score += player.score || 0;
        teamStat.lines += player.lines || 0;
        teamStat.roundWins += player.roundWins || 0;
        teamStat.players += 1;
      }
    });

    return stats;
  }, [gameState.players]);

  const allPlayers = useMemo(() => {
    const players: PlayerState[] = [];
    gameState.players.forEach((player) => {
      if (player.isDedicatedServer) return;
      players.push(player);
    });
    return players;
  }, [gameState.players]);

  const sortedPlayers = useMemo(() => {
    const sorted = [...allPlayers];
    sorted.sort((a, b) => {
      const aVal = a[activeSort] || 0;
      const bVal = b[activeSort] || 0;

      if (activeSort === "deaths") {
        return aVal - bVal;
      }
      return bVal - aVal;
    });
    return sorted;
  }, [allPlayers, activeSort]);

  const goalStat = useMemo(() => {
    const gameEndType = gameState.params?.gameEndType || "frag";
    if (gameEndType === "score") {
      return "score" as SortColumn;
    } else if (gameEndType === "lines") {
      return "lines" as SortColumn;
    } else {
      return "frags" as SortColumn;
    }
  }, [gameState.params?.gameEndType]);

  const winningTeam = useMemo(() => {
    const teamsSortedByGoal = Array.from(teamStats.entries())
      .filter(([_, stats]) => stats.players > 0)
      .sort(([teamIdA, statsA], [teamIdB, statsB]) => {
        const aVal = statsA[goalStat] || 0;
        const bVal = statsB[goalStat] || 0;
        if (goalStat === "deaths") {
          return aVal - bVal;
        }
        return bVal - aVal;
      });

    if (teamsSortedByGoal.length === 0) return null;
    return teamsSortedByGoal[0][0];
  }, [teamStats, goalStat]);

  const activeTeams = useMemo(() => {
    const teams = Array.from(teamStats.entries())
      .filter(([_, stats]) => stats.players > 0)
      .sort(([teamIdA, statsA], [teamIdB, statsB]) => {
        const aVal = statsA[activeSort] || 0;
        const bVal = statsB[activeSort] || 0;
        if (activeSort === "deaths") {
          return aVal - bVal;
        }
        return bVal - aVal;
      });
    return teams;
  }, [teamStats, activeSort]);

  const playersByTeam = useMemo(() => {
    const byTeam = new Map<number, PlayerState[]>();
    sortedPlayers.forEach((player) => {
      const team = player.team ?? 0;
      if (!byTeam.has(team)) {
        byTeam.set(team, []);
      }
      byTeam.get(team)!.push(player);
    });
    return byTeam;
  }, [sortedPlayers]);

  const localPlayer = gameState.localConnectionId
    ? gameState.connections.get(gameState.localConnectionId)
    : null;
  const localTeam = localPlayer?.team ?? 0;
  const localNumPlayer = localPlayer?.numPlayer;

  const handleSort = (column: SortColumn) => {
    setActiveSort(column);
  };

  const handlePageChange = (page: string) => {
    if (page === "rank" || page === "speed") {
      setActivePage(page);
    }
  };

  return (
    <ResultScreenLayout>
      <ResultHeader winningTeam={winningTeam} isSinglePlayer={false} />
      <ResultTabs
        activePage={activePage}
        onPageChange={handlePageChange}
        isSinglePlayer={false}
        showHighScores={false}
      />
      <div className="result-screen-content">
        {activePage === "rank" && (
          <RankTable
            players={sortedPlayers}
            playersByTeam={playersByTeam}
            activeTeams={activeTeams}
            activeSort={activeSort}
            onSort={handleSort}
            localConnectionId={gameState.localConnectionId}
            localNumPlayer={localNumPlayer}
            localTeam={localTeam}
            isSinglePlayer={false}
          />
        )}
        {activePage === "speed" && (
          <SpeedTable
            playersByTeam={playersByTeam}
            activeTeams={activeTeams}
            localConnectionId={gameState.localConnectionId}
            localNumPlayer={localNumPlayer}
            localTeam={localTeam}
            isSinglePlayer={false}
          />
        )}
      </div>
      <ResultFooter
        onQuit={onQuit}
        onRestart={onRestart}
        isSinglePlayer={false}
      />
    </ResultScreenLayout>
  );
};

export default MultiPlayerResultScreen;
